import SelfMoveButton from './SelfMoveButton'
import React, { useEffect, useState } from 'react'
import {
  bookingRestricted,
  bookingsManagerBookableSpaceEmployees,
  hasLocationManageRights,
  isNeighborhood,
  isPrimary,
  isSecondary,
  isShared,
  spaceEmployees,
  isMeetingRoom,
  isViewer,
  isIT,
} from '@utils'
import get from 'lodash/get'
import find from 'lodash/find'
import filter from 'lodash/fp/filter'
import getOr from 'lodash/fp/getOr'
import map from 'lodash/fp/map'
import compose from 'lodash/fp/compose'
import css from './seatableSpaceCard.module.scss'
import {
  isLocationAdmin,
  isDepartmentAdmin,
  isSpaceCaptain,
  isBookingManager,
  hasDeptManagableSearchRights,
} from '@utils'
import { DateUtil, TIME } from '@utils/dateUtil'
import { EmployeeSelect, Flex, IconButton, Toaster } from '@ui'
import { Alert, Intent, Position, TextArea } from '@blueprintjs/core'
import { getDefaultTimes } from '@utils'
import api, {
  IBooking,
  ICurrentUser,
  IEmployee,
  ISpace,
  IUploadedImageAsset,
  RecurrencePatternType,
  Role,
} from '@api'
import useSettings from 'components/NewSettings/hooks/useSettings'
import { useUser } from '@hooks'
import EmployeeBookingCard, { EmployeeDetailCard } from '../Card/EmployeeCard'
import { useBookingDesk } from '@providers'
import { useFloor } from '@providers/mapProvider'
import getSpaceAsset from 'components/SpaceTypeManagement/hooks/useSpaceAsset'
import { getSpaceImage } from '..//SpaceCatalog'
import SpaceDetailCard from './SpaceDetailCard'
import useBookingModal from 'components/BookingsModal/hooks/useBookingModal'
import Select, { SelectItem } from '@ui/Select'
import { Size } from '@ui/Avatar'
import { handleGraphQLErrors } from '@utils/error'
import { Button } from '@runeffective/evo-design-system'
import { hotelingDesk } from '@ui/canvas/utils'

export const MAX_CHARACTERS_FOR_NOTE = 250

interface SeatableCardT {
  space: ISpace
  toggleModal: () => void
}

const SeatableSpaceCard = ({ space, toggleModal }: SeatableCardT) => {
  const [selectedEmployee, setBookingSelectedEmployee] = useState(null as IEmployee | null)
  const [bookingManagerCreteBookingFor, setBookingManagerCreteBookingFor] = useState(false)
  const [bookingNote, setBookingNote] = useState('')
  const [imgAssets, setImgAssets] = useState<IUploadedImageAsset[]>([])
  const [imgIndx, setImgIndx] = useState(0)

  const setValidImgIndx = (indx: number) => {
    let val = imgIndx + indx
    val = val < 0 ? 0 : val === imgAssets.length ? imgAssets.length - 1 : val
    setImgIndx(val)
  }

  const settings = useSettings()
  const { user } = useUser()
  const { fetchBookings } = useBookingModal()
  const floor = useFloor()
  const { bookDesk, recurrence, bookRecurringDesk, bookMultiDayDesk, date } = useBookingDesk()

  const isAdminPage = true
  const isFinderPage = false

  const { id, bookings = [] } = space
  const deskType = get(space, 'usageType.displayName', '')
  const isMeetingRoomSpace = isMeetingRoom(space.spaceType)
  const isRecurrence = recurrence.typePattern !== 'never'
  const timezone = get(floor, 'map.building.timezone', DateUtil.defaultTz())

  // Roles
  const { role = Role.VIEWER } = user || { employee: {} }
  const canManageAllEmployees =
    user?.bookingManagerSetting && user?.bookingManagerSetting.manage_all
  const isLocationManageRights = hasLocationManageRights(user?.employee, floor.floorId || '')
  const isDepartmentRole = isDepartmentAdmin(role) || isSpaceCaptain(role)
  const isBookingManagerRole = isBookingManager(role)
  const isLocationManager = isSpaceCaptain(role) || isLocationAdmin(role)
  const isViewerOrITRole = isViewer(role) || isIT(role)

  const isHotelingDesk = hotelingDesk(space.usageType)
  const employeeId = selectedEmployee ? selectedEmployee.id : user?.employee.id || ''
  const employeeDetails = isBookingManagerRole
    ? bookingsManagerBookableSpaceEmployees(space, user?.bookingManagerSetting)
    : spaceEmployees(space)

  let hasBookingsToday = 0
  let optionsValue: {
    data: IEmployee
    title: string
    value: string
  }[] = []
  if (employeeDetails && employeeDetails.length && (isAdminPage || isBookingManagerRole)) {
    optionsValue = employeeDetails.map((employee) => ({
      data: employee,
      title: employee.id === user?.employee.id ? `${employee.name} (me)` : employee.name,
      value: employee.id,
    }))
  }

  const filterOptionforDept = (node: IEmployee) => {
    let val = false
    if (user?.employee.id === node.id) {
      val = true
    } else if (hasDeptManagableSearchRights(user, node)) {
      val = true
    }

    return val
  }

  if (isDepartmentRole) {
    optionsValue = optionsValue.filter((i) => filterOptionforDept(i.data))
  }

  if (isFinderPage) {
    //Count the number of bookings that have a start or and end date today
    hasBookingsToday = bookings.reduce((count: number, { startDate: s, endDate: e }) => {
      const start = new Date(s.replace(/\s/, 'T').replace(' UTC', '.000Z'))
      const end = new Date(e.replace(/\s/, 'T').replace(' UTC', '.000Z'))
      const m = DateUtil.tz(timezone)
      const isEndToday = m.isSame(DateUtil.tz(end, timezone), TIME.DAYS)
      const isStartToday = m.isSame(DateUtil.tz(start, timezone), TIME.DAYS)

      if (isEndToday || isStartToday) {
        count++
      }

      return count
    }, 0)
  }

  const startTime = DateUtil.tz(timezone)
  // rounding start time to the nearest 15min interval
  // making booking 60 mins
  // closest15minutes(startTime)

  const endTime = startTime.clone().add(1, TIME.HOURS)

  const canBookNextHour = bookings
    .filter((booking) => {
      // elim unnecessary dates
      const bookingStart = DateUtil.tz(booking.startDate, timezone)
      const bookingEnd = DateUtil.tz(booking.endDate, timezone)

      return (
        bookingStart.isSame(startTime, TIME.DAYS) ||
        (bookingEnd.isSame(endTime, TIME.DAYS) && !booking.autoReleasedAt)
      )
    })
    .reduce((result, current) => {
      if (!result) return result // term early if conflict already found

      const bookingStart = DateUtil.tz(current.startDate, timezone)
      const bookingEnd = DateUtil.tz(current.endDate, timezone)

      return (
        result &&
        !startTime.isBetween(bookingStart, bookingEnd) &&
        !endTime.isBetween(bookingStart, bookingEnd)
      )
    }, true)

  const defaultDates = getDefaultTimes(
    floor.bookingWorkdayTimeFrom || '',
    floor.bookingWorkdayTimeTo || '',
    timezone
  )

  const searchEmployeesWithDepartmentFilter = async (query: string) => {
    // if (!query) {
    //   return Promise.resolve([])
    // }
    return api.user.searchDeptAdmin({ query }).then(({ edges }) => {
      const options = edges.reduce(
        (a: { value: string; title: string; data: IEmployee }[], { node }) => {
          if (filterOptionforDept(node)) {
            a.push({
              value: node.id,
              title: user?.employee.id == node.id ? `${node.name} (me)` : node.name,
              data: node,
            })
          }
          return a
        },
        []
      )
      return options
    })
  }

  const correctStartTime = startTime.isSameOrAfter(defaultDates.timeFrom, TIME.HOURS)
  const correctEndTime = endTime.isSameOrBefore(defaultDates.timeTo, TIME.HOURS)

  // const showBookingComponentAndAssets = isHoteling(space.usageType) && isFinderPage

  const getBookingByEmployee = (id: string) => find(space.bookings, { employee: { id } })
  const allSpaceBookingIds = getIds(space.bookings)
  const selectedBookings = getSpaceBooking(space, date.fromDate, date.toDate) //createMapBookings(space.id, allSpaceBookingIds)

  const restrictions =
    !isAdminPage && !isBookingManagerRole && bookingRestricted({ user, space, settings, deskType })

  const canCreateBookingFor = floor.isBookDeskPage && bookings.length === 0
  const isCreateBookingForShown =
    (!isLocationManager && canCreateBookingFor) || (isLocationManageRights && canCreateBookingFor)

  const isBookNowDisabled = isLocationManager
    ? (selectedEmployee === null && isCreateBookingForShown) ||
      bookingNote.length > MAX_CHARACTERS_FOR_NOTE
    : (selectedEmployee === null && !isViewerOrITRole) ||
      bookingNote.length > MAX_CHARACTERS_FOR_NOTE

  const bookingSuccessfullyCreated = () => {
    Toaster.success({
      message: 'Booking successfully created',
    })
    floor.selection?.clear()
    employeeId === user?.employee.id && fetchBookings()
    floor.reloadMap?.()
  }

  useEffect(() => {
    getSpaceAsset(id).then((assets) => {
      setImgAssets(assets)
    })
  }, [id])

  return (
    <div className={css.container}>
      <div className={css.popupCloseButton}>
        <IconButton
          style={{ marginBottom: 8 }}
          round
          minimal
          large
          iconName="times"
          tooltip="Employee Catalog"
          tooltipPosition={Position.RIGHT}
          onClick={toggleModal}
        />
      </div>
      <Flex className={css.block} direction="column">
        <Flex className={css.block}>
          <div className={css.blockGap}>
            <div className={css.imageContainer}>
              {getSpaceImage(imgAssets[imgIndx]?.url, space)}
            </div>
            {imgAssets[imgIndx] && (
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-evenly' }}>
                {
                  <IconButton
                    style={{ marginBottom: 8 }}
                    round
                    minimal
                    disabled={imgIndx === 0}
                    iconName="arrow-left"
                    // tooltip="Previous"
                    // tooltipPosition={Position.LEFT}
                    onClick={() => setValidImgIndx(-1)}
                  />
                }
                <div className="pt-1">
                  {imgIndx + 1} / {imgAssets.length}
                </div>
                {
                  <IconButton
                    style={{ marginBottom: 8 }}
                    round
                    minimal
                    disabled={imgIndx + 1 === imgAssets.length}
                    iconName="arrow-right"
                    // tooltip="Next"
                    // tooltipPosition={Position.RIGHT}
                    onClick={() => setValidImgIndx(1)}
                  />
                }
              </div>
            )}
          </div>
          <div>
            <SpaceDetailCard
              space={space}
              smallText={space.spaceAssignment.department?.name}
              showAvatar
              showEmployees
              showWpg
              showAsset
              hideDept
              showAssetModal
            />
          </div>
        </Flex>
        {!isMeetingRoomSpace &&
          !isViewerOrITRole &&
          !bookings.length &&
          isCreateBookingForShown && (
            <div>
              <div>
                <div className={css.createBookingTitle}>Create booking for:</div>
                {optionsValue.length > 0 ||
                (optionsValue.length === 0 && employeeDetails.length > 0) ? (
                  <Select<IEmployee>
                    width="100%"
                    onItemSelect={(param) => {
                      setBookingSelectedEmployee(param.data as IEmployee)
                    }}
                    itemListPredicate={(query, items) => {
                      let list: SelectItem<IEmployee>[] = []
                      list = items.filter(
                        (f) => (f.data?.name || '').toLowerCase().indexOf(query.toLowerCase()) > -1
                      )
                      return list
                    }}
                    itemRenderer={(item, { handleClick }) => (
                      <div style={{ width: 410 }} onClick={handleClick}>
                        <EmployeeDetailCard
                          name={item?.title as string}
                          avatar
                          email={item?.data?.email}
                          showEmail
                          avatarProps={{ size: Size.XS }}
                        />
                      </div>
                    )}
                    noResults={<div style={{ minWidth: 316 }}>No results</div>}
                    title={selectedEmployee?.name || 'Search Employee'}
                    items={optionsValue}
                  />
                ) : (
                  <EmployeeSelect
                    placeholder={'Search employee'}
                    onChange={(param) => setBookingSelectedEmployee(param)}
                    buttonProps={{ style: { width: 421, backgroundColor: 'white' } }}
                    value={selectedEmployee}
                    list={api.user.searchDeptAdmin}
                    itemListPredicate={(query, items) => {
                      if (!query && user && isHotelingDesk) {
                        return [
                          {
                            id: user.employee.id,
                            name: `${user.employee.name} (me)`,
                            email: user?.employee.email,
                          },
                        ]
                      }
                      const val = query
                        ? items.filter(
                            (i) =>
                              (isDepartmentRole
                                ? filterOptionforDept({
                                    ...i,
                                    dept: { id: i.department?.id || '', name: '' },
                                  })
                                : true) && i?.name.toLowerCase().indexOf(query.toLowerCase()) > -1
                          )
                        : isDepartmentRole
                        ? items.filter((i) =>
                            filterOptionforDept({
                              ...i,
                              dept: { id: i.department?.id || '', name: '' },
                            })
                          )
                        : items
                      return val
                    }}
                  />
                )}
              </div>
            </div>
          )}
        <div>
          {settings.bookingNotesEnabled && (
            <BookingNoteCard
              bookingNote={bookingNote}
              setBookingNote={setBookingNote}
              maxCharLength={MAX_CHARACTERS_FOR_NOTE}
            />
          )}
        </div>
      </Flex>

      {bookings.length === 0 ? (
        restrictions ? (
          <AnimatedAlert type="info">{<p>{restrictions}</p>}</AnimatedAlert>
        ) : isAdminPage || isBookingManagerRole ? (
          <React.Fragment>
            <span className={css.buttonContainer}>
              <Button
                size="small"
                layout="secondary"
                style={{ display: 'inline' }}
                onClick={toggleModal}>
                Cancel
              </Button>
              <Button
                size="small"
                layout="primary"
                style={{ display: 'inline', marginLeft: 10, padding: '6px 10px' }}
                disabled={isBookNowDisabled}
                onClick={() => {
                  if (isRecurrence) {
                    bookRecurringDesk({
                      employeeId,
                      spaceId: space.id,
                      note: bookingNote,
                      startDate: date.fromDate,
                      endDate: date.toDate || date.fromDate,
                    })
                      .then(() => {
                        bookingSuccessfullyCreated()
                        floor.reloadMap?.(date.fromDate, date.toDate)
                      })
                      .catch(handleGraphQLErrors)
                  } else {
                    if (isAdminPage) {
                      bookMultiDayDesk({
                        employeeId,
                        spaceId: space.id,
                        startDate: date.fromDate,
                        endDate: date.toDate || date.fromDate,
                        note: bookingNote,
                      })
                        .then(() => {
                          bookingSuccessfullyCreated()
                          floor.reloadMap?.(date.fromDate, date.toDate)
                        })
                        .catch(handleGraphQLErrors)
                    } else {
                      bookDesk({
                        employeeId,
                        spaceId: space.id,
                        note: bookingNote,
                        startDate: date.fromDate,
                        endDate: date.toDate || date.fromDate,
                      })
                        .then(() => {
                          bookingSuccessfullyCreated()
                          floor.reloadMap?.(date.fromDate, date.toDate)
                        })
                        .catch(handleGraphQLErrors)
                    }
                  }
                }}>
                Book Now
              </Button>
            </span>
          </React.Fragment>
        ) : (
          !floor.isBookDeskPage &&
          !floor?.isBookMeetingRoomPage &&
          isViewerOrITRole && (
            <React.Fragment>
              {settings.bookingNotesEnabled && (
                <BookingNoteCard
                  bookingNote={bookingNote}
                  setBookingNote={setBookingNote}
                  maxCharLength={MAX_CHARACTERS_FOR_NOTE}
                />
              )}
              <span className={css.buttonContainer}>
                <Button
                  size="default"
                  layout="primary"
                  style={{ display: 'inline' }}
                  onClick={() => {
                    bookDesk({
                      spaceId: space.id,
                      employeeId,
                      note: bookingNote,
                      startDate: date.fromDate,
                      endDate: date.toDate || date.fromDate,
                    }).then(() => {
                      bookingSuccessfullyCreated()
                    })
                  }}
                  disabled={
                    bookingNote.length > MAX_CHARACTERS_FOR_NOTE ||
                    (settings.hourlyBookingEnabled
                      ? !(canBookNextHour && correctEndTime && correctStartTime)
                      : hasBookingsToday > 0 ||
                        !DateUtil.tz(timezone).isBetween(
                          defaultDates.timeFrom,
                          defaultDates.timeTo
                        ))
                  }>
                  {settings.hourlyBookingEnabled ? 'Book 1 Hour' : 'Book Today'}
                </Button>
                <Button size="small" onClick={toggleModal}>
                  Book Date
                </Button>
              </span>
            </React.Fragment>
          )
        )
      ) : (
        floor.isBookDeskPage &&
        bookings.map((b) => (
          <EmployeeBookingCard space={space} timezone={timezone} booking={b} showEmail />
        ))
      )}
    </div>
  )
}
const isDisabled = (space: ISpace, user: ICurrentUser | null) => {
  if (isPrimary(space.usageType) || isSecondary(space.usageType)) {
    return space.spaceAssignment.employee?.id === user?.id ? false : true
  }

  if (isShared(space.usageType)) {
    const emp = space.spaceAssignment.sharedEmployees?.map((emp) => emp.id)
    return !emp?.includes(user?.id || '')
  }

  if (isNeighborhood(space.usageType)) {
    const emp = space.spaceAssignment.neighborhood?.employees.map((emp) => emp.id)
    return !emp?.includes(user?.id || '')
  }
  return false
}

const getIds = map('id')
const createMapBookings = (spaceId: string, bookingIds: string[]) =>
  compose(
    map((booking: IBooking) => {
      const isMultiDay =
        booking.bookingSchedule &&
        booking.bookingSchedule.recurrencePattern === RecurrencePatternType.MULTI_DAY

      return {
        ...booking,
        isMultiDay,
        isRecurrence: booking.bookingSchedule && !isMultiDay,
      }
    }),
    filter((booking: IBooking) => bookingIds.includes(booking.id)),
    getOr([], `[${spaceId}].bookings`)
  )

const getSpaceBooking = (space: ISpace, fromDate: DateUtil, toDate: DateUtil | null) => {
  return null
}

export const BookingNoteCard = ({
  bookingNote,
  setBookingNote,
  maxCharLength,
  width,
}: {
  bookingNote: string
  setBookingNote: (note: string) => void
  maxCharLength: number
  width?: number | string
}) => {
  return (
    <React.Fragment>
      <TextArea
        style={{ width }}
        className={css.note}
        value={bookingNote}
        placeholder={'Note'}
        onChange={(e) => setBookingNote(e.target.value)}
      />
      <div className={css.noteCharacters}>
        {bookingNote.length <= maxCharLength ? (
          <span>
            {bookingNote.length}/{maxCharLength} characters
          </span>
        ) : (
          <span className={css.charError}>Character limit exceeded !</span>
        )}
      </div>
    </React.Fragment>
  )
}

const AnimatedAlert = ({
  type,
  children,
  style = {},
}: {
  type: string
  children: JSX.Element
  style?: any
}) => (
  <div>
    <Alert style={style}>{children}</Alert>
  </div>
)

export default SeatableSpaceCard

/*


*/
